import React from 'react';
import {Drawer} from 'antd';
import type {OrderSubmissionSnapshot, SubmissionStatus} from '../types';

interface PendingSignalOrderPanelProps {
    open: boolean;
    snapshot: OrderSubmissionSnapshot;
    status: SubmissionStatus;
    message: string | null;
    querying: boolean;
    onQuery: () => void;
    onClose: () => void;
}

const STATUS_LABEL: Partial<Record<SubmissionStatus, string>> = {
    submitting: '⏳ 订单提交中',
    processing: '⏳ 订单结果确认中，请勿重复下单',
    unknown: '⚠ 暂未确认订单结果，请勿创建新订单',
    under_review: '🛡 订单已提交审核，等待风控结果',
    succeeded: '✅ 订单已成功提交',
    failed: '❌ 订单处理失败',
};

/**
 * 页面刷新或交易上下文切换后的只读恢复面板。
 * 只消费已冻结快照并按原幂等键查询，绝不重建或重新 POST 订单。
 */
export const PendingSignalOrderPanel: React.FC<PendingSignalOrderPanelProps> = ({
                                                                                    open,
                                                                                    snapshot,
                                                                                    status,
                                                                                    message,
                                                                                    querying,
                                                                                    onQuery,
                                                                                    onClose,
                                                                                }) => {
    const terminal = 'succeeded' === status || 'failed' === status;
    return (
        <Drawer title="订单结果确认" placement="right" width={420} open={open}
                onClose={onClose} destroyOnHidden className="ai-quick-trading-drawer">
            <div className="ai-qt-content">
                <div className={`ai-qt-status ai-qt-status-${status}`} role="status">
                    {STATUS_LABEL[status] ?? '订单状态确认中'}
                    {message ? ` · ${message}` : ''}
                </div>
                <div className="ai-qt-summary">
                    <div className="summary-title">已冻结订单摘要</div>
                    <Summary label="合约" value={snapshot.instId}/>
                    <Summary label="方向" value={'long' === snapshot.posSide ? '开多' : '开空'}/>
                    <Summary label="交易意图" value={snapshot.tradeIntent}/>
                    <Summary label="订单类型" value={'market' === snapshot.orderType ? '市价' : '限价'}/>
                    <Summary label="委托价"
                             value={'market' === snapshot.orderType ? '市价' : formatPrice(snapshot.px)}/>
                    <Summary label="目标位" value={formatPrice(snapshot.takeProfitPrice)}/>
                    <Summary label="止损位" value={formatPrice(snapshot.stopLossPrice)}/>
                    <Summary label="开仓比例" value={`${snapshot.allocationPercent}%`}/>
                    <Summary label="杠杆" value={`${snapshot.lever}x`}/>
                    <Summary label="幂等键" value={snapshot.idempotencyKey}/>
                </div>
                <div className="ai-qt-actions">
                    <button className="ai-qt-cancel" onClick={onClose}>关闭</button>
                    {!terminal && (
                        <button className="ai-qt-submit" disabled={querying} onClick={onQuery}>
                            {querying ? '查询中...' : '查询结果'}
                        </button>
                    )}
                </div>
                <div className="ai-qt-risk-warning">查询只读取原订单状态，不会再次提交订单</div>
            </div>
        </Drawer>
    );
};

const Summary: React.FC<{ label: string; value: string }> = ({label, value}) => (
    <div className="summary-row"><span>{label}</span><span>{value}</span></div>
);

function formatPrice(value?: number): string {
    return null == value || !Number.isFinite(value) ? '--' : `$${value.toLocaleString()}`;
}
